/**
 * Typed client wrappers for the v2 reporting catalog and query endpoints.
 *
 * Endpoints live under /api/reporting/ (added in backend Chunk B) and are
 * the single source of truth for what data the picker can offer and how
 * queries against ModelSpecs are validated.
 */
import { apiClient } from './client';

// ── Types matching backend FieldSpec.to_catalog() / ModelSpec.to_catalog() ──

export type FieldRole = 'value' | 'label' | 'category' | 'filter';

export type FieldType =
  | 'number'
  | 'currency'
  | 'date'
  | 'datetime'
  | 'category'
  | 'yes_no'
  | 'choice'
  | 'multi_choice'
  | 'text';

export type AggregateType =
  | 'sum'
  | 'average'
  | 'min'
  | 'max'
  | 'count_all'
  | 'count_distinct'
  | 'latest'
  | 'percent_change'
  | 'percent_of_total';

export interface CatalogFieldSpec {
  key: string;
  label: string;
  type: FieldType;
  roles: FieldRole[];
  group_path: string[];
  unit: string | null;
  default_aggregate: AggregateType | null;
  supported_aggregates: AggregateType[];
  compatible_dimensions: string[];
  subject_type: string;
  description: string;
  tabular_visible: boolean;
  /**
   * A retired field. It still resolves — a shipped key never changes
   * meaning and never moves — but it is absent from the catalog unless
   * a widget already holds it, so it stops being offered for new ones.
   */
  deprecated?: boolean;
  /** The field that covers this one and more. Not a rename. */
  superseded_by?: string | null;
  /** Sentence to show on a widget still using a retired field. */
  deprecation_notice?: string;
  /**
   * The value a FILTER field behaves as when no condition on it is set —
   * e.g. ``meter_level`` → "Main meters", ``scope2_method`` →
   * "Location-based". The backend applies it; this lets the filter UI say
   * what an unfiltered widget is already showing. ``null`` = no default.
   */
  filter_default?: string | null;
  /**
   * Org-specific note for a widget with NO condition on this field, e.g.
   * "Includes leased-out meters — add a Control boundary filter to
   * exclude them" on ``control_boundary``. ``null`` when there is nothing
   * to say for this organisation (it has no such readings). Shown by the
   * filter bars only while the field is unfiltered; never changes a
   * number.
   */
  unset_notice?: string | null;
  /**
   * Present only when the catalog was asked about a specific measure,
   * and only when the answer was not "yes". ``hidden`` fields are
   * dropped server-side, so anything that arrives here is disabled with
   * an explanation and, where one exists, a field to use instead.
   */
  compatibility?: {
    reason: string;
    hidden: boolean;
    disabled: boolean;
    message: string;
    alternative: string | null;
  };
}

export interface CatalogModelSpec {
  key: string;
  label: string;
  group: string;
  icon: string | null;
  description: string;
  /**
   * A retired source. Still in the payload — a widget already on one
   * has to resolve its model, or the builder refuses to open it and the
   * dashboard filter merge stops pruning filters this source cannot
   * accept — but not offered for new widgets.
   */
  deprecated?: boolean;
  default_widget: {
    label?: string;
    value?: string;
    aggregate?: string;
    viz?: string;
    /**
     * Conditions the widget builder pre-applies when a NEW widget is
     * started on this source (e.g. Energy: Control boundary is
     * Operational control). Visible and removable like any other rule;
     * saved widgets are never changed.
     */
    filters?: StructuredFilters;
  } | null;
  fields: CatalogFieldSpec[];
}

/** A measure the user can ask for, independent of which source answers
 *  it. Appears ONCE in the picker however many sources can serve it —
 *  the population is derived from the rest of the widget and explained
 *  back via /resolve/. */
export interface CanonicalMeasure {
  key: string;
  label: string;
  entity: string;
  description: string;
  /** How many sources can serve this. Informational only — the user
   *  never chooses between them; the planner routes from the measure to
   *  whatever breakdowns were asked for. */
  source_count?: number;
  /** The resolution triple for this measure: the ModelSpec key
   *  (``source``), the value FieldSpec key (``value_field``) and the
   *  default aggregate. A picked canonical measure maps directly to
   *  ``(model_spec_key=source, value_field_key=value_field,
   *  aggregate=aggregate)`` with no ``/resolve`` round-trip. Optional —
   *  only populated on catalogs that carry the triple. */
  source?: string | null;
  value_field?: string | null;
  aggregate?: string;
  /** The home field's unit (e.g. ``"kWh"``, ``"kg CO₂e"``). Lets the
   *  Strategy / commit-band unit pickers default and narrow to the measure's
   *  own unit. Optional — only populated on catalogs that carry it; ``null``
   *  when the home field has no fixed unit. */
  unit?: string | null;
}

/** A way of splitting or filtering, independent of source. Unlike a
 *  measure, the same dimension reached two ways IS the same dimension,
 *  so these dedupe cleanly. */
export interface CanonicalDimension {
  key: string;
  label: string;
  entity: string;
  description: string;
  source_count: number;
}

export interface CatalogResponse {
  models: CatalogModelSpec[];
  measures?: CanonicalMeasure[];
  dimensions?: CanonicalDimension[];
  /** ``spec:field`` pairs a canonical measure already speaks for. The
   *  picker drops these from the per-source list so one idea does not
   *  appear both ways, and keeps everything else. */
  canonical_covered_fields?: string[];
  /** Same, for dimensions — ``spec:field`` pairs a canonical dimension
   *  already speaks for. */
  canonical_covered_dimension_fields?: string[];
}

/** What /resolve/ answers: which population a widget is counting, in a
 *  sentence, plus the concrete source + field keys to build it from. */
export interface ResolveResponse {
  resolved: boolean;
  source?: string;
  measure_field?: string;
  dimension_fields?: Record<string, string>;
  explanation?: string;
  alternatives?: Array<{ source: string; population: string }>;
  compatible_dimensions?: string[];
  reason?: string;
  conflicting?: string[];
}

export async function resolveMeasure(
  organisationId: number | string,
  measure: string,
  dimensions: string[] = [],
): Promise<ResolveResponse> {
  const qs = new URLSearchParams({
    organisation_id: String(organisationId),
    measure,
  });
  if (dimensions.length) qs.set('dimensions', dimensions.join(','));
  return apiClient.get<ResolveResponse>(`/api/reporting/resolve/?${qs}`);
}

// ── Query request / response ──

/** Time-bucket granularity for date / datetime group-by axes. Engine
 * wraps the axis in TruncMonth / TruncWeek / etc., so one widget can
 * surface "spend per month" without a row per raw transaction date. */
export type DateTruncGranularity = 'day' | 'week' | 'month' | 'quarter' | 'year';

export interface QueryRequest {
  model: string;
  mode?: 'aggregate' | 'table';
  value_field?: string;
  aggregate?: AggregateType;
  group_by?: string;
  /** Secondary group-by — produces (label, category, value) rows. */
  category?: string;
  /** Time-bucket a date-typed group-by / category axis. Ignored for
   *  non-date axes; ``undefined`` keeps raw (per-row) grouping. */
  date_trunc?: DateTruncGranularity;
  filters?: StructuredFilters | Record<string, unknown>;
  date_range?: { start: string; end: string };
  limit?: number;
  /** Table-viz row offset for pagination (mode='table'). */
  offset?: number;
  /** Table-viz server-side search term — matched (icontains) across the
   * table's text columns so it spans the whole result set, not just the
   * loaded page. Only used for mode='table'. */
  search?: string;
  /** Table-viz column projection — the field keys to project, in order,
   * mirroring the saved widget's Columns shelf. Only used for mode='table';
   * absent means project every visible field. */
  columns?: string[];
  use_cache?: boolean;
  /** Per-widget options for the value field's Python aggregator (e.g.
   * ``{ radius_km: 75 }`` for ``local_spend_pct``). Forwarded into
   * the engine and folded into the cache key so widgets with
   * different options don't collide. */
  value_field_options?: Record<string, unknown>;
  /** KPI tiles set this so the backend attaches a trailing-month sparkline
   * + previous-period delta (``kpi`` block on the response). Only honoured
   * for a scalar aggregate (no group_by / category). */
  kpi_trend?: boolean;
}

/** Trailing-month sparkline + previous-period comparison for a KPI tile.
 * Present only when the request set ``kpi_trend`` and the query is a
 * scalar aggregate; the backend omits it (and the tile degrades to just
 * the headline number) whenever it can't be computed. */
export interface KpiTrend {
  /** Oldest→newest monthly points over the trailing window. */
  trend: Array<{ period: string; value: number }>;
  /** The headline value (basis for the delta). */
  current: number | null;
  /** Same metric over the equal-length period immediately before the
   * KPI's date range; null when the KPI isn't period-scoped. */
  previous: number | null;
  /** Signed percent change of ``current`` vs ``previous``; null when
   * there's no comparable previous period. */
  delta_pct: number | null;
}

export interface AggregateResponse {
  mode: 'aggregate';
  rows: Array<Array<string | number | null>>;
  columns: string[];
  cache_hit: boolean;
  query_ms: number;
  row_count: number;
  /** Optional KPI sparkline + comparison — see {@link KpiTrend}. */
  kpi?: KpiTrend;
}

export interface TabularResponse {
  mode: 'table';
  rows: Array<Record<string, unknown>>;
  columns: Array<{
    key: string;
    label: string;
    type: FieldType;
    unit: string | null;
    /** Field description, shown as a header tooltip. The calculation
     * audit columns rely on it to carry caveats (mixed units, raw
     * currency) that don't fit in a header. */
    description?: string;
  }>;
  row_count: number;
  truncated: boolean;
  query_ms: number;
  /** Pagination — the row index this page starts at. */
  offset?: number;
  /** Whether a further page exists after this one. */
  has_more?: boolean;
  /** Total matching rows across all pages; null when it couldn't be
   * counted (falls back to has_more-driven Prev/Next). */
  total_count?: number | null;
  /** Requested columns this mode can't project — typically fields
   * computed across rows (FX-converted Spend) that have no per-row
   * value. Surfaced so the UI can explain the absence rather than
   * letting the column silently never appear. */
  dropped_columns?: Array<{ key: string; label: string; reason: string }>;
}

export type QueryResponse = AggregateResponse | TabularResponse;

// ── Client wrappers ──

// In-flight request coalescing for read-only reporting GETs (Bug D).
// On org switch the app mounts several reporting surfaces at once
// (DashboardView, WidgetBuilderV2, Navbar, NewsAlerts, …), each of
// which independently fetches the catalog / dashboards / org-summary
// within the same tick. That burst of duplicated requests overwhelmed
// the browser's DNS resolver (net::ERR_NAME_NOT_RESOLVED) and piled
// load onto the single-headroom worker. Coalescing returns the SAME
// in-flight promise for identical concurrent calls, so each endpoint
// fires once per org. The entry clears as soon as it settles — this is
// request de-duplication, NOT a cache, so data stays fresh.
const inflightReportingGets = new Map<string, Promise<unknown>>();

function coalesceGet<T>(key: string, run: () => Promise<T>): Promise<T> {
  const existing = inflightReportingGets.get(key) as Promise<T> | undefined;
  if (existing) return existing;
  const p = run().finally(() => {
    // Only clear if this promise is still the registered one — a later
    // call that started after this settled may have re-registered.
    if (inflightReportingGets.get(key) === p) {
      inflightReportingGets.delete(key);
    }
  });
  inflightReportingGets.set(key, p);
  return p;
}

/** Fetch the reporting catalog scoped to the user's current organisation. */
export async function fetchCatalog(
  organisationId: number | string,
  // Field keys a widget already holds. Retired fields are absent from
  // the catalog so they stop being offered for NEW widgets — but the
  // builder resolves a saved widget's config against that same catalog,
  // so without this a retired key hydrates as null: the shelf comes up
  // empty, Run stays disabled, and Save writes the widget back without
  // its value field. The tile keeps rendering, so it fails quietly at
  // exactly the moment someone opens it to edit.
  using?: readonly string[],
): Promise<CatalogResponse> {
  // Sorted and deduped so two widgets holding the same keys in a
  // different order share one cache entry rather than two.
  const keys = Array.from(new Set(using ?? [])).filter(Boolean).sort();
  const query = keys.length
    ? `&using=${encodeURIComponent(keys.join(','))}`
    : '';
  return coalesceGet(`catalog:${organisationId}:${keys.join(',')}`, () =>
    apiClient.get<CatalogResponse>(
      `/api/reporting/catalog/?organisation_id=${organisationId}${query}`,
    ),
  );
}

export interface FilterOption {
  // Numeric for FK ids (supplier_id, country_id…); string for enum
  // codes (Claim.status = "verified"/"submitted"/…). The backend
  // sends whichever the underlying ORM lookup expects.
  id: number | string;
  name: string;
}

/** Fetch the available filter values for a (model, field) pair. */
export async function fetchFilterOptions(
  organisationId: number | string,
  modelKey: string,
  fieldKey: string,
): Promise<FilterOption[]> {
  const res = await apiClient.get<{ options: FilterOption[] }>(
    `/api/reporting/filter-options/?organisation_id=${organisationId}&model=${encodeURIComponent(
      modelKey,
    )}&field=${encodeURIComponent(fieldKey)}`,
  );
  return res.options || [];
}

// Structured filter shape — see backend reporting_engine._decompose_filters.
export interface FilterCondition {
  field: string;
  values: Array<number | string>;
  /** Match operator (default ``'in'``). ``'in'`` keeps a record when its
   * value is one of ``values`` ("is"); ``'not_in'`` keeps it when the
   * value is NONE of ``values`` ("is not"). Absent means ``'in'`` so
   * every existing saved widget behaves exactly as before. Date-range
   * conditions ignore this (they're always a between). */
  op?: 'in' | 'not_in';
  /** How the rule is applied (v1 parity — see
   * ``components/reporting-v2/displayFilters.ts``):
   *  - ``'data'`` (default): narrows the engine query (WHERE clause,
   *    pre-aggregation) — changes totals. Sent to the engine.
   *  - ``'display'``: hides non-matching rows/series on the chart,
   *    applied client-side AFTER aggregation — totals unchanged. NOT
   *    sent to the engine.
   * Absent ``mode`` means ``'data'`` so existing saved widgets are
   * unchanged. */
  mode?: 'data' | 'display';
}

export interface StructuredFilters {
  logic: 'and' | 'or';
  conditions: FilterCondition[];
}

/** Backend response when the query exceeded the inline budget and
 * has been queued for a Celery worker. The frontend polls
 * ``poll_url`` until the result is ready and returns it as if the
 * call had been synchronous all along. Callers of ``runQuery``
 * don't need to know which path served them. */
interface QueuedQueryResponse {
  status: 'queued';
  job_id: string;
  poll_url: string;
}

/** Job-status response from ``GET /api/reporting/query/jobs/<uuid>/``.
 * Shape mirrors ``ReportingQueryJob`` in
 * ``backend/calculations/models.py``; ``result`` is populated only
 * once the job has reached a terminal status. */
interface JobStatusResponse {
  job_id: string;
  status: 'queued' | 'running' | 'succeeded' | 'failed';
  result?: QueryResponse;
  http_status?: number;
  error?: string;
  duration_ms?: number | null;
  created_at?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
}

function isQueuedResponse(
  payload: QueryResponse | QueuedQueryResponse,
): payload is QueuedQueryResponse {
  return (
    payload != null &&
    typeof payload === 'object' &&
    (payload as QueuedQueryResponse).status === 'queued' &&
    typeof (payload as QueuedQueryResponse).job_id === 'string'
  );
}

// Adaptive backoff for the async-job poll. Quick queries that fall
// off the inline fast-path usually finish in the first or second poll.
// Heavier queries fall through to longer waits so we don't hammer the
// server.
//
// The backend long-poll was shortened to ~1s (it holds a gunicorn
// thread + DB connection for the whole wait, and a dashboard polls many
// jobs concurrently — a long server wait pins the tiny pool and times
// out unrelated endpoints). This client schedule is lengthened in step:
// a ~1s server wait + these delays keeps the effective poll cadence (and
// so the total poll count for a slow job) roughly where it was under the
// old 5s server wait, while each individual poll frees its thread /
// connection ~5× faster.
const POLL_DELAYS_MS = [0, 500, 1000, 2000, 4000, 6000, 9000];
// Hard cap on the number of poll round-trips for a single job. With the
// backoff schedule above (ramping to 9s) plus the ~1s server wait, ~45
// attempts is ~5 minutes of elapsed polling — on the order of the
// worker's ~5 minute soft_time_limit. A job still pending after that has
// either crashed silently or the worker is down, so we stop and surface
// an error rather than poll forever. Bounds the loop on attempt count
// per the repo's "every polling loop must be bounded" rule. (Lowered
// from 120 when the backoff was lengthened, to keep the wall-clock bound
// at ~5 min rather than letting the slower schedule stretch it to ~20.)
const MAX_POLL_ATTEMPTS = 45;
// Stop after this many *consecutive* failed status reads. A flaky
// network or a momentarily-saturated worker can 502/timeout on a
// status GET; one or two of those are recoverable, but a sustained
// run means the job is unreachable — bail with a clean error instead
// of hammering a dead endpoint. Resets to 0 on any successful read.
const MAX_CONSECUTIVE_POLL_ERRORS = 3;

/** Thrown when a caller aborts a poll via its ``AbortSignal`` (org /
 * dashboard switch, tile unmount). Carries a stable ``name`` so
 * callers can distinguish a deliberate cancel from a genuine query
 * failure and swallow it silently rather than surfacing an error. */
export class PollCancelledError extends Error {
  constructor(message = 'Query polling cancelled.') {
    super(message);
    this.name = 'PollCancelledError';
  }
}

/** True when an error originated from a caller-requested cancel. */
export function isPollCancelled(err: unknown): boolean {
  return err instanceof PollCancelledError;
}

/** Poll ``/api/reporting/query/jobs/<id>/`` until the job reaches a
 * terminal status, then return the result as a ``QueryResponse`` (or
 * throw if the job failed). Used internally by ``runQuery`` when the
 * backend's inline fast-path fell through to the async fallback.
 *
 * Bounded on three axes so a slow / dead job can't poll forever:
 *  - ``MAX_POLL_ATTEMPTS`` total round-trips, then a clean timeout error;
 *  - ``MAX_CONSECUTIVE_POLL_ERRORS`` back-to-back status-read failures,
 *    then a clean error (no silent infinite retry);
 *  - an optional ``signal`` that, once aborted, stops the loop before
 *    the next poll so navigating away cancels the job's polling
 *    instead of leaving it running in the background.
 *
 * On a bound being hit the loop STOPS — it never re-submits the query.
 * Re-running is the caller's explicit choice (reload). */
async function pollQueryJob(
  jobId: string,
  signal?: AbortSignal,
): Promise<QueryResponse> {
  const aborted = () => signal?.aborted === true;
  let consecutiveErrors = 0;
  for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt++) {
    if (aborted()) throw new PollCancelledError();
    const delay =
      POLL_DELAYS_MS[Math.min(attempt, POLL_DELAYS_MS.length - 1)];
    if (delay > 0) {
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
    // Re-check after the wait — the caller may have navigated away
    // while we were sleeping. Bail before firing the next request so
    // no zombie poll outlives the dashboard that started it.
    if (aborted()) throw new PollCancelledError();
    let res: JobStatusResponse;
    try {
      res = await apiClient.get<JobStatusResponse>(
        `/api/reporting/query/jobs/${jobId}/`,
      );
    } catch (_err) {
      // A failed status read (502 / network blip / timeout) is NOT a
      // failed query — don't re-submit. Count consecutive failures
      // and give up cleanly once they exceed the cap.
      if (aborted()) throw new PollCancelledError();
      consecutiveErrors += 1;
      if (consecutiveErrors >= MAX_CONSECUTIVE_POLL_ERRORS) {
        throw new Error(
          'Lost contact with the query while waiting for it to finish. ' +
            'Reload to retry.',
        );
      }
      continue;
    }
    consecutiveErrors = 0;
    if (res.status === 'succeeded') {
      if (!res.result) {
        // Shouldn't happen — the backend only writes succeeded
        // once result_json is populated. Guard anyway so we throw
        // a comprehensible error instead of returning undefined.
        throw new Error('Query succeeded but returned no result.');
      }
      return res.result;
    }
    if (res.status === 'failed') {
      throw new Error(res.error || 'Query failed.');
    }
    // Still queued or running — loop and poll again. The backend
    // long-polls so each round-trip already incorporates some wait
    // on its side; the client-side delays above are an additional
    // backoff layer.
  }
  // Ran out of attempts without a terminal status. The query is still
  // out there (the worker may yet finish it) but we stop polling and
  // surface an actionable error — we do NOT re-submit it.
  throw new Error(
    'Query timed out — reload to retry. Try narrowing filters or a shorter date range.',
  );
}

/** Run an aggregate or tabular query against a ModelSpec.
 *
 * Transparent over the backend's inline fast-path and async
 * fallback: queries that complete within the engine's inline budget
 * return their result immediately; slower queries are queued onto a
 * Celery worker and this helper polls until the result is ready.
 * Callers always get a ``Promise<QueryResponse>`` and never need to
 * branch on which path served them.
 *
 * Pass ``signal`` to cancel the async-fallback polling when the caller
 * is torn down (org / dashboard switch, tile unmount). The initial
 * POST always runs; if it queues, the poll loop stops at the next
 * iteration once the signal aborts (throwing ``PollCancelledError``)
 * so no zombie job keeps polling after the caller is gone. */
export async function runQuery(
  organisationId: number | string,
  body: QueryRequest,
  signal?: AbortSignal,
): Promise<QueryResponse> {
  const response = await apiClient.post<QueryResponse | QueuedQueryResponse>(
    `/api/reporting/query/?organisation_id=${organisationId}`,
    body,
  );
  if (isQueuedResponse(response)) {
    return pollQueryJob(response.job_id, signal);
  }
  return response;
}

// ── Batch query endpoint (POST /api/reporting/queries/) ─────────────

/** One query in a batch — ``tileId`` identifies the destination tile
 * the result is written back to; ``body`` is the same shape ``runQuery``
 * accepts. */
export interface BatchQueryItem {
  tileId: string;
  body: QueryRequest;
}

/** Per-tile envelope returned by the batch endpoint. ``ok`` carries
 * the engine result inline (``status`` + the flattened QueryResponse
 * fields); ``queued`` carries a job to poll; ``error`` carries the
 * failure. Mirrors the backend ``_batch_envelope_to_wire`` shape. */
type BatchOkEnvelope = { status: 'ok' } & QueryResponse;
interface BatchQueuedEnvelope {
  status: 'queued';
  job_id: string;
  poll_url: string;
}
interface BatchErrorEnvelope {
  status: 'error';
  error: string;
  code?: string;
}
type BatchEnvelope =
  | BatchOkEnvelope
  | BatchQueuedEnvelope
  | BatchErrorEnvelope;

interface BatchQueriesResponse {
  results: Record<string, BatchEnvelope>;
}

/** Resolved per-tile outcome handed back to the caller. ``queued``
 * envelopes are transparently polled to completion (or rejection)
 * before this resolves, so callers only ever see a final ``ok`` or
 * ``error`` — exactly as ``runQuery`` hides the async fallback. */
export type BatchTileResult =
  | { status: 'ok'; result: QueryResponse }
  | { status: 'error'; error: string; code?: string };

/** Run several queries in ONE request and return a ``Map`` keyed by
 * ``tileId``.
 *
 * The dashboard previously fired one ``runQuery`` per visible widget —
 * ~9+ parallel POSTs on load. ``runQueries`` collapses that into a
 * single ``POST /api/reporting/queries/`` so the server runs them
 * sequentially over one DB context. Any tile whose query overran the
 * inline budget comes back ``queued``; we reuse the existing
 * ``pollQueryJob`` to drive each to a final result, so callers get a
 * settled ``ok`` / ``error`` per tile and never branch on the async
 * fallback themselves. A failed poll is surfaced as that tile's
 * ``error`` rather than rejecting the whole batch.
 *
 * Pass ``signal`` to cancel any queued-tile polling spawned by this
 * batch when the caller is torn down (org / dashboard switch). The
 * single batch POST always runs; each per-tile poll loop stops at its
 * next iteration once aborted, and the cancelled tiles are left out of
 * the returned map rather than recorded as errors (a cancel isn't a
 * failure — the caller has already moved on). */
export async function runQueries(
  organisationId: number | string,
  items: BatchQueryItem[],
  signal?: AbortSignal,
): Promise<Map<string, BatchTileResult>> {
  const out = new Map<string, BatchTileResult>();
  if (items.length === 0) return out;

  const response = await apiClient.post<BatchQueriesResponse>(
    `/api/reporting/queries/?organisation_id=${organisationId}`,
    { queries: items.map((it) => ({ tile_id: it.tileId, ...it.body })) },
  );
  const results = response.results || {};

  // Resolve queued envelopes by polling their jobs. Everything else
  // settles synchronously. Collect the poll promises so the function
  // doesn't resolve until every tile has a final outcome.
  const pending: Array<Promise<void>> = [];
  for (const item of items) {
    const envelope = results[item.tileId];
    if (!envelope) {
      // The server dropped this tile (shouldn't happen — every tile we
      // send carries a tile_id). Surface as an error so the caller
      // clears its loading state instead of spinning forever.
      out.set(item.tileId, {
        status: 'error',
        error: 'No result returned for this widget.',
      });
      continue;
    }
    if (envelope.status === 'ok') {
      // Strip the ``status`` discriminator back off to recover the
      // plain QueryResponse the rest of the app consumes.
      const { status: _status, ...result } = envelope;
      out.set(item.tileId, { status: 'ok', result: result as QueryResponse });
    } else if (envelope.status === 'queued') {
      pending.push(
        pollQueryJob(envelope.job_id, signal)
          .then((result) => {
            out.set(item.tileId, { status: 'ok', result });
          })
          .catch((err: unknown) => {
            // A deliberate cancel (caller torn down) isn't a tile
            // error — leave it out of the map so the caller's stale
            // guards drop it silently rather than flashing an error.
            if (isPollCancelled(err)) return;
            out.set(item.tileId, {
              status: 'error',
              error: err instanceof Error ? err.message : 'Query failed.',
            });
          }),
      );
    } else {
      out.set(item.tileId, {
        status: 'error',
        error: envelope.error || 'Query failed.',
        code: envelope.code,
      });
    }
  }

  if (pending.length > 0) await Promise.all(pending);
  return out;
}

// ── Admin: snapshot refresh ───────────────────────────────────────

/** Response shape from POST /api/calculations/reporting/refresh-snapshots/.
 * ``status: "queued"`` (HTTP 202) when Celery accepted the task — the
 * actual rebuild runs in the worker. ``status: "done_sync"`` (HTTP 200)
 * when the broker was unavailable and the view ran the refresh inline.
 * Either way ``org_ids`` echoes which orgs were targeted. */
export interface RefreshSnapshotsResponse {
  status: 'queued' | 'done_sync';
  org_ids: number[] | null;
  errors?: { org_id: number; error: string }[];
}

/** Trigger a manual rebuild of the v2 reporting snapshot/rollup tables.
 *
 * Superuser-only. Pass ``organisationId`` to refresh one org; omit to
 * refresh every org with at least one Purchase (same default as the
 * nightly Beat task).
 *
 * The result fires after dispatch, not after the rebuild finishes —
 * the worker can take several minutes for a large customer. Watch the
 * widgets recover (or CloudWatch logs) to confirm completion. */
export async function refreshReportingSnapshots(
  organisationId?: number | string,
): Promise<RefreshSnapshotsResponse> {
  const qs = organisationId != null ? `?organisation_id=${organisationId}` : '';
  return apiClient.post<RefreshSnapshotsResponse>(
    `/api/calculations/reporting/refresh-snapshots/${qs}`,
    {},
  );
}

// ── Widget / dashboard CRUD ───────────────────────────────────────

/** Chart appearance options surfaced on the Settings tab. All optional
 * — undefined falls back to ChartViz's defaults. Persisted under
 * ``WidgetV2Config.appearance`` so save/load round-trips. */
export interface WidgetAppearance {
  showLegend?: boolean;
  legendPosition?: 'top' | 'bottom' | 'left' | 'right';
  /** When true, value labels are drawn statically on the marks; when false
   * (default) they appear only on hover. The builder surfaces this as a
   * "Data labels: On hover / Always shown" select. */
  showDataLabels?: boolean;
  /** Show cartesian gridlines. Defaults to true (Plotly's default). No-op on
   * pie / treemap / radar / map vizzes, which have no cartesian grid. */
  showGridLines?: boolean;
  /** Decimal places for displayed values — data labels, hover, KPI number,
   * table cells. Defaults to 2. Whole numbers still render without decimals.
   * Does not change axis-tick formatting (kept on Plotly's smart default). */
  decimalPlaces?: number;
  sortOrder?: 'asc' | 'desc' | 'none';
  topN?: number;
  /** Table viz with a CATEGORY (secondary group-by): ``'pivot'`` (default)
   * spreads each category across its own column; ``'rows'`` keeps it as a
   * single column with one row per (label, category) pair, e.g. Product |
   * Supplier | Spend, so the value column can be sorted. Display-only. */
  tableLayout?: 'pivot' | 'rows';
  startAtZero?: boolean;
  /** For KPI / number tiles: which direction of the comparison chip counts
   * as good, so it can colour semantically. ``'up'`` — higher is better
   * (scores, coverage); ``'down'`` — lower is better (emissions, spend,
   * consumption); ``'neutral'`` (default) — no good/bad meaning, chip stays
   * tonal purple. */
  improvementDirection?: 'up' | 'down' | 'neutral';
  /** Fix the value axis to a 0–100 range — for metrics that are already
   * percentages (% of suppliers with claim, % of spend, coverage). Sets
   * the Plotly value-axis ``range`` to ``[0, 100]`` so every bar/point is
   * read against a full 100% scale instead of auto-scaling to the data
   * max. Applies to the value axis of cartesian charts (bar / line /
   * area): the y-axis on vertical charts, the x-axis on horizontal bars.
   * No-op on pie / treemap / map / box / bubble vizzes. Takes precedence
   * over ``startAtZero`` (an explicit range always starts at 0). Defaults
   * to false. */
  capAxisAt100?: boolean;
  /** 0.0 — 1.0; defaults to 1. Useful for stacked bars where solid
   * blocks overlap and hide layered data. */
  opacity?: number;
  /** Overlay saved events on time-axis charts as vertical lines +
   * labels. No-op on non-time charts. Defaults to false. */
  showEvents?: boolean;
  /** Overlay ``Installation.install_date`` markers as events. Pulled
   * from /api/reporting/installation-events/ at render time;
   * independent of ``showEvents`` so the user can have either source
   * on / off on its own. Defaults to false. */
  showInstallationEvents?: boolean;
  /** When ``showEvents`` is on, restrict the overlay to these manual
   * event ids. ``undefined`` (the default) overlays ALL of them — so
   * existing widgets and future events are included automatically. */
  eventIds?: Array<number | string>;
  /** When ``showInstallationEvents`` is on, restrict the overlay to
   * these installation overlay ids (``installation:<id>``). ``undefined``
   * overlays ALL of them. */
  installationEventIds?: Array<number | string>;
  /** Render the pie chart as a donut by punching a hole in the middle.
   * No-op on non-pie charts. Defaults to false. */
  donut?: boolean;
  /** Combo / dual-axis mode for multi-value bar charts. When the user
   * has dropped 2 fields onto the VALUES shelf and ticks this, the
   * first series renders as bars on the primary Y axis and the second
   * as a line on a secondary Y axis (right side). No-op unless the
   * response is the multi-value synthetic-category shape with exactly
   * two series. */
  dualAxis?: boolean;
  /** Normalise a stacked bar chart so every category's stack sums to
   * 100% (Plotly ``barnorm: 'percent'``) — the v1 "normalise to 100%"
   * option. Shows the share each series contributes rather than
   * absolute totals. No-op unless the viz is a stacked bar. Defaults
   * to false. */
  stackNormalize?: boolean;
  /** Map viz layer mode. ``sites`` (default) renders one layer keyed
   * off the LABEL; ``suppliers`` renders supplier sites only;
   * ``both`` renders buyer sites + supplier sites + optional links. */
  mapLayers?: 'sites' | 'suppliers' | 'both';
  /** Draw lines between buyer-site → supplier-site pairs that have a
   * SupplyRelationship. Meaningful in ``mapLayers === 'both'`` mode
   * only. */
  mapShowLines?: boolean;
  /** Colour supplier bubbles by a metric: ``single`` (uniform) or
   * ``buckets`` (per-point colour based on ``mapBucketField`` and the
   * thresholds in ``mapBuckets``). Default ``single``. */
  mapColorMode?: 'single' | 'buckets';
  /** Which per-supplier metric drives the bucket lookup. Only
   * ``supplier_score`` is supported today (percentage from the
   * latest CalculationRun); other fields land as the supplier-graph
   * payload grows. */
  mapBucketField?: 'supplier_score';
  /** Threshold table for the bucket colouring. Each entry colours
   * supplier sites whose bucket-field value falls in
   * ``[min, max)``. Ordering doesn't matter; the chart picks the
   * first match. */
  mapBuckets?: MapColorBucket[];
  /** Multi-tier traceability depth. ``1`` (default) walks only direct
   * suppliers; ``2`` includes suppliers-of-suppliers; ``3`` is the
   * cap. The server bounds the total point count so a cyclic graph
   * can't run away. */
  mapMaxTier?: 1 | 2 | 3;
  /** WRI Resource Watch / Global Forest Watch tile-overlay key (e.g.
   * ``tree_cover_loss``). Rendered as a raster source below the
   * trace markers on the scattermap basemap. ``undefined`` = no
   * overlay. See ``WRI_LAYERS`` in ChartViz for the supported keys. */
  mapWriLayer?: string;
  /** Opacity 0–1 for the WRI raster overlay. Default 0.5 so the
   * underlying basemap and supplier bubbles stay visible. */
  mapWriOpacity?: number;
  /** Legacy single-radius setting for ``local_spend_pct`` (km).
   * Superseded by ``localRadiusBrackets`` — kept so widgets saved
   * before the brackets feature still render. When both are present,
   * brackets win. */
  localRadiusKm?: number;
  /** Unit users picked for the local-sourcing radius widget. Affects
   * both how typed bracket values are interpreted (mi → km internally
   * via × 1.609344) and the bracket labels rendered on the chart. The
   * engine always works in km, so the cache key isn't unit-dependent. */
  localDistanceUnit?: 'km' | 'mi';
  /** Ordered list of radius brackets (in ``localDistanceUnit``) for
   * the cumulative-bracket render: a bracket of 30 km captures spend
   * with suppliers within 30 km, 50 km captures everything within
   * 50 km (which by definition includes the 30 km bucket), and so on.
   * One series per bracket on the chart. */
  localRadiusBrackets?: number[];
  /** Per-column conditional formatting for the table viz, keyed by field
   * key. ``risk`` colours Low/Medium/High/Unclassified cells with the
   * brand risk palette; ``pace`` colours progress status
   * ahead/on_track/behind/completed; ``heat`` shades a numeric column
   * light→dark by value; ``score`` buckets a numeric column by quantile
   * (bottom 30% pink, middle 30% amber, top 40% green); ``none`` leaves it
   * plain. Columns absent from the map render plain. Only applies to the
   * table visualisation. */
  columnFormats?: Record<
    string,
    'risk' | 'heat' | 'pace' | 'score' | 'bar' | 'none'
  >;
  /** Text widget (#261) content — markdown-free plain text rendered with
   * preserved line breaks. Only used when ``viz === 'text'``; the widget
   * carries no data query.
   *
   * Legacy single-block fields (``textContent`` + ``textSize``) are still
   * read for back-compat. New text widgets use the three structured blocks
   * below (heading / subheading / body) so one widget can hold all three
   * at their own font scales. */
  textContent?: string;
  /** Text widget size preset — controls the rendered font scale (legacy
   * single-block widgets only). */
  textSize?: 'small' | 'medium' | 'large';
  /** Structured text-widget blocks — any subset may be set. Rendered
   * stacked: heading (large) → subheading (medium) → body (small). */
  textHeading?: string;
  textSubheading?: string;
  textBody?: string;
}

/** Conversion factor — 1 international statute mile in kilometres. */
export const MI_TO_KM = 1.609344;

/** Default brackets shown when a ``local_spend_pct`` widget hasn't
 * been customised yet. Sized to match the prior fixed FieldSpecs
 * (50, 100, 250) but with 30 added so users see meaningful
 * differentiation in tight regional sourcing. Unit-agnostic — the
 * numbers are interpreted in whichever unit the widget is configured
 * for. */
export const DEFAULT_LOCAL_RADIUS_BRACKETS: number[] = [30, 50, 100, 250];

/** Convert a user-supplied radius to km for the engine. */
export function radiusToKm(radius: number, unit: 'km' | 'mi' | undefined): number {
  return unit === 'mi' ? radius * MI_TO_KM : radius;
}

/** Format a bracket label like ``"≤ 30 km"`` for chart axes /
 * legends. Uses the bracket's natural value (no conversion), the
 * widget's unit, and a leading ``≤`` to signal the cumulative
 * "within this radius" semantic. */
export function bracketLabel(radius: number, unit: 'km' | 'mi' | undefined): string {
  return `≤ ${radius} ${unit === 'mi' ? 'mi' : 'km'}`;
}

/** Curated set of WRI / Global Forest Watch tile layers. Order is
 * deforestation first, then broader land-cover. Exposed publicly so
 * both the picker UI and the renderer share the same source of
 * truth. */
export const WRI_LAYERS: Record<string, { name: string; url: string }> = {
  tree_cover_loss: {
    name: 'Tree cover loss',
    url: 'https://tiles.globalforestwatch.org/umd_tree_cover_loss/v1.8/tcd_30/{z}/{x}/{y}.png',
  },
  tree_cover_loss_fires: {
    name: 'Tree cover loss from fires',
    url: 'https://tiles.globalforestwatch.org/umd_tree_cover_loss_from_fires/v20220324/tcd_30/{z}/{x}/{y}.png',
  },
  co2_tree_cover_loss: {
    name: 'CO₂ from tree cover loss',
    url: 'https://tiles.globalforestwatch.org/whrc_aboveground_biomass_loss/v4.1.7_2/Mg_ha-1__tcd_30/{z}/{x}/{y}.png',
  },
  land_cover_2015: {
    name: 'Global land cover (2015)',
    url: 'https://s3.amazonaws.com/wri-tiles/global-landcover-2015/{z}/{x}/{y}.png',
  },
};

export interface MapColorBucket {
  min: number;
  max: number;
  color: string;
  label: string;
}

/** Sensible default thresholds for a supplier-score colour-band map —
 * traffic-light split tuned to the brand palette. */
export const DEFAULT_MAP_BUCKETS: MapColorBucket[] = [
  { min: 0, max: 33, color: '#ff1261', label: 'Low' },
  { min: 33, max: 66, color: '#ffd62f', label: 'Medium' },
  { min: 66, max: 100, color: '#49b76a', label: 'High' },
];

/** Full v2 widget config the builder rehydrates from. */
export interface WidgetV2Config {
  /** Persisted display title — separate from the saved-widget row's
   * ``name`` so the user can rename in the builder and still preview
   * the title before saving. */
  title?: string;
  /** Optional one-paragraph description shown under the title. */
  description?: string;
  model: string;
  value_field?: string;
  aggregate?: AggregateType;
  /** Additional VALUES shelf entries beyond the primary ``value_field``
   * + ``aggregate``. Persists the multi-value builder state so combo /
   * dual-axis bar charts survive a save/load round-trip. The dashboard
   * runner fires N+1 parallel queries (one per entry, plus the primary)
   * and stitches them with a synthetic category, matching the builder
   * preview. Empty / undefined means single-value (existing behaviour). */
  extra_values?: Array<{
    value_field: string;
    aggregate: AggregateType;
    display_unit?: string;
  }>;
  group_by?: string;
  category?: string;
  /** Time-bucket granularity for date / datetime group-by axes. */
  date_trunc?: DateTruncGranularity;
  filters?: StructuredFilters;
  date_range?: {
    preset?: string;
    start?: string | null;
    end?: string | null;
  };
  viz?: string;
  mode?: 'aggregate' | 'table';
  /** Field keys the user dropped onto a table widget's Columns shelf, in
   * order. Only meaningful for the table viz (mode='table'); when present
   * the engine projects exactly these columns instead of every field. */
  columns?: string[];
  /** Presentation unit override for the value column — empty means
   * automatic magnitude-aware promotion. */
  display_unit?: string;
  /** Chart appearance overrides — applied on top of ChartViz defaults. */
  appearance?: WidgetAppearance;
  /** Installation-matrix viz config. Carried instead of the
   * engine model/value/group-by fields because the matrix is a
   * bespoke pivot served by ``/api/reporting/installation-matrix/``
   * rather than the aggregate engine. ``installation_ids`` are the
   * columns; ``site_ids`` optionally restricts the rows; ``status_in``
   * optionally filters which lifecycle statuses count as "installed". */
  installation_matrix?: {
    installation_ids: number[];
    site_ids?: number[];
    status_in?: string[];
  };
  /** Price-change-tracker viz config. Like ``installation_matrix`` this is
   * a bespoke widget that owns its own fetch
   * (``/api/activity/purchases/top-products/``) rather than the aggregate
   * engine, so it carries no model/value/group-by. ``limit`` optionally
   * caps the number of products shown (the server caps at 1000); the date
   * window comes from the widget's ``date_range``. */
  price_change_tracker?: {
    limit?: number;
  };
}

export interface WidgetEvent {
  // Manual widget events use an integer pk; installation-source
  // overlays use a string id of the form ``installation:<inst_id>``
  // so the two sources can coexist in one list without collisions.
  id: number | string;
  widget_id?: number;
  date: string; // YYYY-MM-DD
  label: string;
  description: string;
  color: string;
  created_by_id?: number | null;
  created_at?: string | null;
  /** Tag for installation-overlay vs manually-typed event. Used by
   *  the chart to render synthetic events with a distinct style and
   *  to hide them from the manual-events edit list. */
  source?: 'installation';
}

export async function listInstallationEvents(
  organisationId: number | string,
  opts?: { statuses?: string[]; installationTypes?: string[] },
): Promise<WidgetEvent[]> {
  const params = new URLSearchParams({
    organisation_id: String(organisationId),
  });
  for (const s of opts?.statuses || []) params.append('status', s);
  for (const t of opts?.installationTypes || []) {
    params.append('installation_type', t);
  }
  const res = await apiClient.get<{ events: WidgetEvent[] }>(
    `/api/reporting/installation-events/?${params.toString()}`,
  );
  return res.events || [];
}

export interface SavedWidgetV2 {
  id: number;
  name: string;
  config: WidgetV2Config;
  layout: Record<string, unknown>;
  is_shared: boolean;
  dashboard_id: number | null;
  owner_id: number | null;
  updated_at: string | null;
  created_at: string | null;
}

export interface SavedDashboardV2 {
  id: number;
  name: string;
  is_shared: boolean;
  owner_id: number | null;
  /** Whether the CALLER may edit this dashboard (owner, or a shared
   * dashboard and the caller's role has ``can_edit_dashboards``). Only on
   * the detail / update responses; absent means "assume editable".
   * Enterprise-only — full authoring (add/edit/delete widgets, rename). */
  can_edit?: boolean;
  /** Whether the caller may edit the LAYOUT only (resize / reorder tiles).
   * True for Pro+ (not just Enterprise) so a Pro org can rearrange its
   * view-only standard dashboard to see all its data. */
  can_edit_layout?: boolean;
  updated_at: string | null;
  created_at: string | null;
  widgets?: SavedWidgetV2[];
  /** Dashboard-level filters applied to every widget on render
   * EXCEPT for fields the widget itself filters on (widget wins).
   * Same StructuredFilters shape the widget builder uses. */
  filters?: StructuredFilters;
  /** The dashboard's own date range, in the same {preset, start, end}
   * shape the widget picker emits. Applied to every widget that does
   * not set one of its own — a widget saying nothing about dates
   * inherits this, while one that names a range overrides it. */
  date_range?: {
    preset: string;
    start: string | null;
    end: string | null;
  };
}

const reportingQs = (organisationId: number | string) =>
  `?organisation_id=${organisationId}`;

export async function listWidgets(
  organisationId: number | string,
): Promise<SavedWidgetV2[]> {
  const res = await apiClient.get<{ widgets: SavedWidgetV2[] }>(
    `/api/reporting/widgets/${reportingQs(organisationId)}`,
  );
  return res.widgets || [];
}

export async function fetchWidget(
  organisationId: number | string,
  widgetId: number,
): Promise<SavedWidgetV2> {
  return apiClient.get<SavedWidgetV2>(
    `/api/reporting/widgets/${widgetId}/${reportingQs(organisationId)}`,
  );
}

export async function createWidget(
  organisationId: number | string,
  body: {
    name: string;
    config: WidgetV2Config;
    is_shared?: boolean;
    dashboard_id?: number | null;
    layout?: Record<string, unknown>;
  },
): Promise<SavedWidgetV2> {
  return apiClient.post<SavedWidgetV2>(
    `/api/reporting/widgets/${reportingQs(organisationId)}`,
    body,
  );
}

export async function updateWidget(
  organisationId: number | string,
  widgetId: number,
  body: Partial<{
    name: string;
    config: WidgetV2Config;
    is_shared: boolean;
    dashboard_id: number | null;
    layout: Record<string, unknown>;
  }>,
): Promise<SavedWidgetV2> {
  return apiClient.patch<SavedWidgetV2>(
    `/api/reporting/widgets/${widgetId}/${reportingQs(organisationId)}`,
    body,
  );
}

export async function deleteWidget(
  organisationId: number | string,
  widgetId: number,
): Promise<void> {
  await apiClient.delete<{ deleted: number }>(
    `/api/reporting/widgets/${widgetId}/${reportingQs(organisationId)}`,
  );
}

export async function listDashboards(
  organisationId: number | string,
): Promise<SavedDashboardV2[]> {
  return coalesceGet(`dashboards:${organisationId}`, async () => {
    const res = await apiClient.get<{ dashboards: SavedDashboardV2[] }>(
      `/api/reporting/dashboards/${reportingQs(organisationId)}`,
    );
    return res.dashboards || [];
  });
}

export async function createDashboard(
  organisationId: number | string,
  name: string,
): Promise<SavedDashboardV2> {
  return apiClient.post<SavedDashboardV2>(
    `/api/reporting/dashboards/${reportingQs(organisationId)}`,
    { name },
  );
}

/** Response from the auto-seed endpoint: ``seeded`` is false (and
 * ``dashboard`` absent) when the org had already been seeded or already has
 * dashboards, so callers must branch on it before using ``dashboard``. */
export interface SeedDefaultDashboardResponse {
  seeded: boolean;
  reason?: string;
  dashboard?: SavedDashboardV2;
}

/**
 * Create the standard starter dashboard for an org that has none, from a
 * frontend-owned template. Idempotent + resurrection-safe server-side: a
 * second call (another tab/device, or after the user deletes it) returns
 * ``{ seeded: false }`` and creates nothing. See
 * ``core.views_widgets_v2.seed_default_dashboard_view``.
 */
export async function seedDefaultDashboard(
  organisationId: number | string,
  template: {
    name: string;
    widgets: Array<{
      name: string;
      config: WidgetV2Config;
      layout?: Record<string, unknown>;
    }>;
  },
): Promise<SeedDefaultDashboardResponse> {
  return apiClient.post<SeedDefaultDashboardResponse>(
    `/api/reporting/dashboards/seed-default/${reportingQs(organisationId)}`,
    template,
  );
}

export async function fetchDashboard(
  organisationId: number | string,
  dashboardId: number,
): Promise<SavedDashboardV2> {
  return apiClient.get<SavedDashboardV2>(
    `/api/reporting/dashboards/${dashboardId}/${reportingQs(organisationId)}`,
  );
}

/** Materialized saved-view payload for a dashboard: every covered tile's
 * precomputed result in ONE response, so loading the dashboard as saved
 * (its saved filters and date range) needs no per-tile query + poll.
 * ``fresh: false`` means no payload is available (none built, or the
 * config / saved filters / currency changed since) — the caller loads
 * tiles live. ``widget_ids_included`` lists the covered tiles; any tile not
 * in it (multi-value / bracket / bespoke) is fetched live.
 * ``dashboard_filters`` is the active saved condition list the payload was
 * built with — serve it only while the filters on screen are the same. */
export interface DashboardDataResponse {
  fresh: boolean;
  computed_at?: string | null;
  widget_ids_included?: number[];
  dashboard_filters?: FilterCondition[];
  payload?: Record<string, QueryResponse>;
}

export async function fetchDashboardData(
  organisationId: number | string,
  dashboardId: number,
): Promise<DashboardDataResponse> {
  return apiClient.get<DashboardDataResponse>(
    `/api/reporting/dashboards/${dashboardId}/data/${reportingQs(organisationId)}`,
  );
}

export async function updateDashboard(
  organisationId: number | string,
  dashboardId: number,
  body: Partial<{
    name: string;
    is_shared: boolean;
    filters: StructuredFilters;
    date_range: { preset: string; start: string | null; end: string | null };
  }>,
): Promise<SavedDashboardV2> {
  return apiClient.patch<SavedDashboardV2>(
    `/api/reporting/dashboards/${dashboardId}/${reportingQs(organisationId)}`,
    body,
  );
}

export async function deleteDashboard(
  organisationId: number | string,
  dashboardId: number,
): Promise<void> {
  await apiClient.delete<{ deleted: number }>(
    `/api/reporting/dashboards/${dashboardId}/${reportingQs(organisationId)}`,
  );
}

// ── Widget events ─────────────────────────────────────────────────

export async function listWidgetEvents(
  organisationId: number | string,
  widgetId: number,
): Promise<WidgetEvent[]> {
  const res = await apiClient.get<{ events: WidgetEvent[] }>(
    `/api/reporting/widgets/${widgetId}/events/${reportingQs(organisationId)}`,
  );
  return res.events || [];
}

export async function createWidgetEvent(
  organisationId: number | string,
  widgetId: number,
  body: { date: string; label: string; description?: string; color?: string },
): Promise<WidgetEvent> {
  return apiClient.post<WidgetEvent>(
    `/api/reporting/widgets/${widgetId}/events/${reportingQs(organisationId)}`,
    body,
  );
}

// ── Site coordinates (for map vizzes) ─────────────────────────────

export interface SiteCoord {
  id: number;
  name: string;
  latitude: number | null;
  longitude: number | null;
}

export async function fetchSiteCoordinates(
  organisationId: number | string,
): Promise<SiteCoord[]> {
  const res = await apiClient.get<{ sites: SiteCoord[] }>(
    `/api/reporting/site-coordinates/${reportingQs(organisationId)}`,
  );
  return res.sites || [];
}

// ── Supply graph (two-layer map mode) ─────────────────────────────

/** One geocoded point on the supply graph — either a buyer site
 * (``site:<id>``) or a supplier site (``supplier:<supplier_id>:<site_id>``).
 * The ``id`` prefix lets us deduplicate across types and key the
 * supply-line endpoints. */
export interface SupplyGraphPoint {
  id: string;
  name: string;
  lat: number;
  lng: number;
  country_name: string | null;
  /** Present for supplier_sites only — the underlying Organisation
   * id of the supplier, used to join score / spend metadata. */
  supplier_id?: number;
  /** Supply-chain depth for this supplier from the requesting org:
   * 1 = direct supplier, 2 = supplier's supplier, 3 = three hops.
   * Always 1 for buyer_sites. */
  tier?: number;
}

export interface SupplyGraphLink {
  fromId: string;
  toId: string;
  distance_km: number | null;
  /** Tier the supplier on the ``to`` end of this link sits at. Used
   * for visual differentiation of upstream chain segments. */
  tier?: number;
}

export interface SupplyGraphSupplierMeta {
  supplier_id: number;
  percentage_score: number | null;
}

export interface SupplyGraph {
  buyer_sites: SupplyGraphPoint[];
  supplier_sites: SupplyGraphPoint[];
  links: SupplyGraphLink[];
  supplier_meta: SupplyGraphSupplierMeta[];
}

export async function fetchSupplyGraph(
  organisationId: number | string,
  options: { maxTier?: 1 | 2 | 3 } = {},
): Promise<SupplyGraph> {
  const tier = options.maxTier && options.maxTier > 1 ? options.maxTier : 1;
  const url = `/api/reporting/supply-graph/${reportingQs(organisationId)}&max_tier=${tier}`;
  return coalesceGet(`supply-graph:${organisationId}:${tier}`, async () => {
    const res = await apiClient.get<SupplyGraph>(url);
    return {
      buyer_sites: res.buyer_sites || [],
      supplier_sites: res.supplier_sites || [],
      links: res.links || [],
      supplier_meta: res.supplier_meta || [],
    };
  });
}

export async function deleteWidgetEvent(
  organisationId: number | string,
  widgetId: number,
  eventId: number,
): Promise<void> {
  await apiClient.delete<{ deleted: number }>(
    `/api/reporting/widgets/${widgetId}/events/${eventId}/${reportingQs(organisationId)}`,
  );
}

// ── Installation matrix (POST /api/reporting/installation-matrix/) ──────
//
// Pivoted sites × installations table. The backend endpoint already
// exists (core/views_reporting.py::installation_matrix_view); these are
// the typed FE wrappers v2 needs to render the matrix viz.

export interface InstallationMatrixColumn {
  key: string;
  label: string;
  installation_id: number;
  installation_type: string | null;
}

export interface InstallationMatrixRow {
  site_id: number;
  site_name: string;
  country: string | null;
  cells: Record<string, string | null>;
}

export interface InstallationMatrixStatusChoice {
  value: string;
  label: string;
}

export interface InstallationMatrix {
  columns: InstallationMatrixColumn[];
  rows: InstallationMatrixRow[];
  status_choices: InstallationMatrixStatusChoice[];
}

/** Fetch the pivoted sites × installations matrix. ``installationIds``
 * are the columns; the optional restrictions narrow the rows /
 * statuses. Returns empty arrays when no installations match. */
export async function fetchInstallationMatrix(
  organisationId: number | string,
  body: {
    installation_ids: number[];
    site_ids?: number[];
    status_in?: string[];
  },
): Promise<InstallationMatrix> {
  const res = await apiClient.post<InstallationMatrix>(
    `/api/reporting/installation-matrix/${reportingQs(organisationId)}`,
    body,
  );
  return {
    columns: res.columns || [],
    rows: res.rows || [],
    status_choices: res.status_choices || [],
  };
}

/** One selectable installation, as returned by
 * ``/api/organisations/installations/``. Used by the matrix viz's
 * column picker in the builder. */
export interface InstallationOption {
  id: number;
  installation_name: string;
  installation_type: string | null;
  site_name: string | null;
  vehicle_name: string | null;
  status: string;
}

/** List the org's installations for the matrix column picker. */
export async function listInstallations(
  organisationId: number | string,
): Promise<InstallationOption[]> {
  const res = await apiClient.get<{ installations: InstallationOption[] }>(
    `/api/organisations/installations/?organisation_id=${organisationId}`,
  );
  return res.installations || [];
}

// ── Org ESG summary (GET /api/reporting/org-summary/) ──────────────────
//
// Backs the three ESG-overview cards at the top of the v2 dashboard.
// Computed by the v2 engine path (org-scoped via middleware) rather
// than the timeout-prone v1 organisation-summary endpoint.

export interface RiskDistribution {
  high: number;
  medium: number;
  low: number;
}

export interface OrgSummary {
  supplier_compliance: {
    total_suppliers: number;
    compliant_suppliers: number;
    pct_compliant: number | null;
  };
  product_risk: {
    deforestation: RiskDistribution;
    modern_slavery: RiskDistribution;
  };
}

/** Fetch the ESG-overview card metrics for the current org. */
export async function fetchOrgSummary(
  organisationId: number | string,
): Promise<OrgSummary> {
  return coalesceGet(`org-summary:${organisationId}`, () =>
    apiClient.get<OrgSummary>(
      `/api/reporting/org-summary/${reportingQs(organisationId)}`,
    ),
  );
}

// ── Frameworks emissions: v2 adapter (migration off v1 widget-data) ─────────
//
// The Frameworks pages (FrameworkDetail / GenericFrameworkDashboard) compute
// disclosure numbers by POSTing to the v1 ``/api/calculations/reporting/
// widget-data/`` endpoint with ``value: 'emissions'`` and v1 ``dimensions``.
// v1 emissions == the v2 ``emissions`` model aggregate (both sum
// ``calculated_emission_value`` over the current EmissionCalculationSnapshot
// rows, same scope / category / date / MARKET_BASED semantics). This adapter
// runs the equivalent v2 query and reshapes the result back into the v1
// ``{ labels, values, series }`` shape the call sites already consume, so a
// migration is a near drop-in swap.

/** v1 emissions ``dimension`` / ``filter`` name → v2 ``emissions`` spec field
 * key. Dimensions the v2 spec doesn't expose yet (``emission_method`` dual
 * reporting; ``utility_type`` energy/consumption) are intentionally absent —
 * the adapter returns ``null`` for those so the caller keeps that specific
 * query on v1 until the spec gains them (Phase 3). */
const V1_EMISSIONS_DIM_TO_V2: Record<string, string> = {
  ghg_scope: 'ghg_scope',
  ghg_category: 'ghg_category',
  sites: 'site_name',
  site: 'site_name',
  date: 'activity_date',
  month: 'activity_date',
  year: 'activity_date',
};

/** The v1-shaped result the Frameworks call sites already consume. */
export interface V1ShapedAggregate {
  labels: string[];
  values: number[];
  /** Present only for split-by / two-dimension queries: one series per
   * category value, aligned to the ``labels`` order (matches v1's over-time
   * ``series`` shape). */
  series?: Array<{ name: string; values: number[] }>;
}

export interface EmissionsAggregateParams {
  /** v1 group-by dimensions (0, 1, or — with a split — 2). */
  dimensions?: string[];
  /** v1 ``{ dimension, values }`` data filters. */
  filters?: Array<{ dimension: string; values: unknown[] }>;
  startDate?: string | null;
  endDate?: string | null;
  /** Time bucket when a date dimension is used. Defaults to ``month``. */
  dateGrouping?: DateTruncGranularity;
  /** v1 secondary ``split_by`` dimension → v2 ``category`` → v1 ``series``. */
  splitBy?: string;
  aggregate?: AggregateType;
}

/** Build the v2 ``QueryRequest`` for a Frameworks emissions query, or ``null``
 * when it uses a dimension/filter the v2 emissions spec can't serve yet (so the
 * caller falls back to v1). Exported for unit testing. */
export function emissionsParamsToV2Query(
  params: EmissionsAggregateParams,
): QueryRequest | null {
  const dims = params.dimensions ?? [];
  const primary = dims[0];
  const secondary = params.splitBy ?? dims[1];

  const mapDim = (d: string | undefined): string | null | undefined =>
    d === undefined ? undefined : V1_EMISSIONS_DIM_TO_V2[d] ?? null;

  const groupBy = mapDim(primary);
  const category = mapDim(secondary);
  // A requested dimension we can't map → bail so the caller keeps v1.
  if (groupBy === null || category === null) return null;

  const isDateAxis =
    groupBy === 'activity_date' || category === 'activity_date';

  let filters: StructuredFilters | undefined;
  if (params.filters && params.filters.length > 0) {
    const conditions: FilterCondition[] = [];
    for (const f of params.filters) {
      const field = V1_EMISSIONS_DIM_TO_V2[f.dimension];
      if (!field) return null; // unmappable filter → keep v1
      // v1 emissions filter values are scope/category ids (string | number).
      conditions.push({
        field,
        values: f.values as Array<string | number>,
        mode: 'data',
      });
    }
    filters = { logic: 'and', conditions };
  }

  return {
    model: 'emissions',
    mode: 'aggregate',
    value_field: 'co2e',
    aggregate: params.aggregate ?? 'sum',
    group_by: groupBy ?? undefined,
    category: category ?? undefined,
    date_trunc: isDateAxis ? params.dateGrouping ?? 'month' : undefined,
    filters,
    date_range:
      params.startDate && params.endDate
        ? { start: params.startDate, end: params.endDate }
        : undefined,
  };
}

const _toFiniteNumber = (v: unknown): number => {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : 0;
};

/** Reshape a v2 ``AggregateResponse`` into the v1 ``{ labels, values, series }``
 * shape. ``hasCategory`` = the query had a secondary dimension (rows are
 * ``[label, category, value]``); otherwise rows are ``[label, value]`` or, with
 * no group-by, ``[value]``. Exported for unit testing. */
export function reshapeEmissionsAggregate(
  res: AggregateResponse,
  hasCategory: boolean,
): V1ShapedAggregate {
  if (hasCategory) {
    const labels: string[] = [];
    const seenLabel = new Set<string>();
    const seriesByCat = new Map<string, Map<string, number>>();
    for (const r of res.rows) {
      const label = r[0] == null ? '—' : String(r[0]);
      const cat = r[1] == null ? '—' : String(r[1]);
      if (!seenLabel.has(label)) {
        seenLabel.add(label);
        labels.push(label);
      }
      if (!seriesByCat.has(cat)) seriesByCat.set(cat, new Map());
      seriesByCat.get(cat)!.set(label, _toFiniteNumber(r[2]));
    }
    const series = Array.from(seriesByCat.entries()).map(([name, byLabel]) => ({
      name,
      values: labels.map((l) => byLabel.get(l) ?? 0),
    }));
    return { labels, values: [], series };
  }
  // No group-by → rows are ``[value]`` (single column); one dimension → rows
  // are ``[label, value]``.
  if (res.columns.length <= 1) {
    return { labels: [], values: res.rows.map((r) => _toFiniteNumber(r[0])) };
  }
  return {
    labels: res.rows.map((r) => (r[0] == null ? '—' : String(r[0]))),
    values: res.rows.map((r) => _toFiniteNumber(r[1])),
  };
}

/** Run a Frameworks emissions query against v2 and return it in the v1
 * ``widget-data`` shape, or ``null`` if v2 can't serve it (caller keeps v1). */
export async function fetchEmissionsAggregateV2(
  organisationId: number | string,
  params: EmissionsAggregateParams,
  signal?: AbortSignal,
): Promise<V1ShapedAggregate | null> {
  const body = emissionsParamsToV2Query(params);
  if (!body) return null;
  const res = await runQuery(organisationId, body, signal);
  if (res.mode !== 'aggregate') return { labels: [], values: [] };
  return reshapeEmissionsAggregate(res, body.category !== undefined);
}
